import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";

export default function AdminLogin() {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user.role !== "admin") {
        logout();
        toast.error("This portal is for administrators only.");
        return;
      }
      toast.success("Welcome to the Admin Portal");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-110px)] flex items-center justify-center bg-brand-navy grain px-6 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white rounded-3xl shadow-lift p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-brand-red flex items-center justify-center text-white"><ShieldCheck className="w-6 h-6" /></div>
          <div>
            <h1 className="font-serif text-3xl text-brand-navy tracking-tight leading-none">Admin Portal</h1>
            <p className="text-xs text-slate-500 mt-1">ImmiLaw.VN — restricted access</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Admin email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-login-email"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-red transition-colors" placeholder="admin@immilaw-vn.com" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Password</label>
            <div className="relative mt-1.5">
              <input required type={show ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-login-password"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-12 outline-none focus:border-brand-red transition-colors" placeholder="••••••••" />
              <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} Enter portal
          </button>
        </form>
        <p className="mt-5 text-xs text-slate-400 text-center">Authorised personnel only. All access is logged.</p>
      </motion.div>
    </div>
  );
}
